/**
 * Session manager — GA4-style analytics sessions that survive redirects,
 * reloads and multiple tabs.
 *
 * The old model minted a fresh `session_id` on every full page load, so a
 * single visit fragmented into many "sessions" (e.g. `/` → redirect to
 * `/login` produced two). This persists the session in localStorage and only
 * rolls it over after 30 minutes of inactivity, matching GA4 semantics:
 *
 *  - `session_id`   stable for the whole visit (all tabs, across reloads)
 *  - `session_number` increments each time a new session starts (returning-user signal)
 *  - `first_visit`  claimed exactly once per device, ever
 *
 * All storage access is guarded so a locked-down/private browser never throws
 * into analytics.
 */

const SESSION_KEY = 'bkfc-analytics-session';
const DEVICE_KEY = 'bkfc-analytics-device';

/** Inactivity gap after which a new session begins (GA4 default). */
export const SESSION_TIMEOUT_MS = 30 * 60 * 1000;

const guid = () =>
  'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
    const r = (Math.random() * 16) | 0;
    return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
  });

const readJSON = key => {
  try {
    const raw = window.localStorage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch (e) {
    return null;
  }
};

const writeJSON = (key, value) => {
  try {
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch (e) {
    // storage full / disabled — session degrades to per-load, still functional
  }
};

/**
 * Resolve the current session, rolling over after inactivity. Call once at the
 * start of every tracked event; it also refreshes the activity timestamp.
 * @param {number} [now] - injectable clock (ms) for tests
 * @returns {{ sessionId: string, sessionNumber: number, isNewSession: boolean }}
 */
export const touchSession = (now = Date.now()) => {
  const prev = readJSON(SESSION_KEY);
  const expired = !prev || !prev.id || now - (prev.lastActivity || 0) > SESSION_TIMEOUT_MS;

  const session = expired
    ? { id: guid(), number: ((prev && prev.number) || 0) + 1, lastActivity: now }
    : { id: prev.id, number: prev.number || 1, lastActivity: now };

  writeJSON(SESSION_KEY, session);
  return { sessionId: session.id, sessionNumber: session.number, isNewSession: expired };
};

/**
 * Claim the one-time first-visit flag for this device. Returns true only the
 * very first time it's ever called on this device (then persists so it never
 * returns true again).
 * @param {number} [now]
 * @returns {boolean}
 */
export const claimFirstVisit = (now = Date.now()) => {
  const device = readJSON(DEVICE_KEY);
  if (device && device.firstSeen) {
    return false;
  }
  writeJSON(DEVICE_KEY, { firstSeen: now });
  return true;
};

/** Test helper — wipe persisted session + device identity. */
export const __resetForTest = () => {
  try {
    window.localStorage.removeItem(SESSION_KEY);
    window.localStorage.removeItem(DEVICE_KEY);
  } catch (e) {
    // ignore
  }
};
